<template>
  <div class="relative flex w-full">
    <!-- outer shell-->
    <div class="bg-neutral-900 w-full p-0 flex flex-col rounded-2xl">
      <div class="h-[300px] lg:h-[464px] flex relative rounded-2xl">
        <div
          v-for="(slide, index) in homeSlides"
          class="absolute top-0 left-0 right-0 bottom-0 flex items-center z-[5] transition-all px-4"
          @click="navigateTo(homeSlides[currentFeatureIndex].btnPath)"
          :class="{
            'opacity-1 translate-y-0': currentFeatureIndex == index,
            'opacity-0 translate-y-[80px]': currentFeatureIndex !== index,
          }"
        >
          <HeadingBasic
            :tag-name="'h2'"
            :size="2"
            class="!text-[32px] lg:!text-[42px] !leading-[1.3] text-center lg:text-left text-balance text-white max-w-[1200px]"
          >
            {{ slide.name }}
          </HeadingBasic>
        </div>
        <div
          :class="[
            `w-full relative z-[2] lg:top-10`,
            'bg-no-repeat bg-cover bg-center bg-white dark:bg-black',
            'rounded-tl-2xl rounded-tr-2xl',
            `before:content-[''] before:inset-0 before:block before:w-full before:h-full before:absolute before:z-[2]`,
          ]"
        >
          <img
            loading="lazy"
            class="absolute right-0 bottom-0 h-full w-full object-cover object-top lg:object-center transition duration-[750ms] delay-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] rounded-tl-2xl rounded-tr-2xl"
            :class="{
              'opacity-1': currentFeatureIndex == 0,
              'opacity-0': currentFeatureIndex !== 0,
            }"
            src="~/assets/images/home-slides/slide1.png"
            alt=""
          />
          <img
            loading="lazy"
            class="absolute right-0 bottom-0 h-full w-full object-cover object-top lg:object-center transition duration-[750ms] delay-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] rounded-tl-2xl rounded-tr-2xl"
            :class="{
              'opacity-1': currentFeatureIndex == 1,
              'opacity-0': currentFeatureIndex !== 1,
            }"
            src="~/assets/images/home-slides/slide2.png"
            alt=""
          />
          <img
            loading="lazy"
            class="absolute right-0 bottom-0 h-full w-full object-cover object-left lg:object-top transition duration-[750ms] delay-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] rounded-tl-2xl rounded-tr-2xl"
            :class="{
              'opacity-1': currentFeatureIndex == 2,
              'opacity-0': currentFeatureIndex !== 2,
            }"
            src="~/assets/images/home-slides/slide3.png"
            alt=""
          />
          <img
            loading="lazy"
            class="absolute right-0 bottom-0 h-full w-full object-cover object-top transition duration-[750ms] delay-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] rounded-tl-2xl rounded-tr-2xl"
            :class="{
              'opacity-1': currentFeatureIndex == 3,
              'opacity-0': currentFeatureIndex !== 3,
            }"
            src="~/assets/images/home-slides/slide4.png"
            alt=""
          />
        </div>
      </div>
      <div class="h-[80px] lg:h-[160px] relative overflow-hidden z-[10]">
        <div class="absolute top-0 left-0 right-0 bottom-0">
          <ul class="flex flex-col lg:flex-row mt-auto">
            <li
              v-for="(slide, index) in homeSlides"
              class="px-6 lg:p-0 cursor-pointer relative group lg:hover:translate-y-0 lg:grow lg:basis-0"
              :class="{
                'hidden lg:block lg:translate-y-[40px] transition-all duration-500':
                  currentFeatureIndex !== index,
              }"
            >
              <NuxtLink
                class=""
                :class="[
                  'hover:bg-neutral-800',
                  'flex group rounded-full lg:rounded-none max-w-[24rem] mx-auto',
                  'pb-4 mt-6 lg:mt-0 lg:pt-8 lg:pb-12',
                  'lg:flex-col justify-center',
                  {
                    'bg-neutral-900': currentFeatureIndex !== index,
                    'bg-cardinal text-white lg:bg-neutral-950': currentFeatureIndex == index,
                  },
                ]"
                :to="slide.btnPath.indexOf('http') === 0 ? slide.btnPath : { path: slide.btnPath }"
                :target="slide.btnPath.indexOf('http') === 0 ? '_blank' : '_self'"
              >
                <span
                  class="hidden lg:block font-bold px-4 relative z-[2] dark:text-gray-200 group-hover:text-cardinal-500 transition-colors text-white text-balance"
                >
                  {{ slide.name }}
                </span>
                <!-- <span class="text-sm text-balance relative z-[2] dark:text-gray-400">{{
                  slide.description
                }}</span> -->
                <span
                  class="font-bold flex gap-2 items-center mt-4 pl-4 text-base lg:text-sm text-center lg:text-left relative z-[5] transition-opacity duration-300 group-hover:opacity-100"
                  style="text-wrap: balance"
                  :class="{
                    'text-cardinal-500': currentFeatureIndex !== index,
                    'text-white lg:text-cardinal-500 opacity-100': currentFeatureIndex == index,
                    'opacity-0': currentFeatureIndex !== index,
                  }"
                >
                  {{ slide.btnText }}
                  <i class="far fa-arrow-right"></i>
                </span>
              </NuxtLink>
              <div class="progress-container bg-white/[0.05] absolute top-0 left-0 right-0 z-[1]">
                <div
                  class="progress-bar group-hover:bg-cardinal"
                  :style="{ width: progress + '%' }"
                  :class="{
                    'opacity-1': currentFeatureIndex == index,
                    'opacity-0': currentFeatureIndex !== index,
                  }"
                ></div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import homeSlidesContent from '~/assets/content/home-slides';
const homeSlides = ref([...homeSlidesContent]);
const currentFeatureIndex = ref(0);
const currentFeature = computed(() => homeSlides.value[currentFeatureIndex.value]);

const progress = ref(0);

const advanceCurrentFeature = () => {
  if (currentFeatureIndex.value == homeSlides.value.length - 1) {
    currentFeatureIndex.value = 0;
  } else {
    currentFeatureIndex.value++;
  }
};

const selectFeature = (index: number) => {
  currentFeatureIndex.value = index;
  startProgress();
};

const interval = ref<NodeJS.Timeout | undefined>();

const startProgress = () => {
  clearInterval(interval.value);
  interval.value = setInterval(() => {
    progress.value += 1;
    if (progress.value >= 100) {
      progress.value = 0;
      advanceCurrentFeature();
    }
  }, 50); // 30 ms * 100 steps = 3000 ms (3 seconds)
};

onMounted(() => {
  startProgress();
});
</script>

<style lang="scss">
.aspect-video {
  aspect-ratio: 16 / 9;
}
.progress-container {
  width: 100%;
  // background-color: #eee;
  border-radius: 4px;
}

.progress-bar {
  @apply h-1 bg-white rounded dark:bg-neutral-200/[0.2] rounded-tl-lg rounded-bl-lg;
  transition: all 0.3s ease;
  will-change: width;
}
</style>
